import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, batch } from 'react-redux';
import { setSolutionJson, setSolutionParam, setTourParameter, setError } from 'actions';
import { clearAll, handleTwoFiles, integrateFileText } from 'utils/apiFilesUploadHelpers';
import { METHODS } from 'components/FloatingPanels/DevConsole/DevConsoleHeader';
import { parseSolutionJson, buildPostMessageFiles } from 'utils/postMessageHelpers';

const WIZARD_INIT_SETTLE_MS = 150;

const usePostMessage = ({ user, oAuth, solution, translations, cookies }) => {
  const dispatch = useDispatch();

  const userRef = useRef(user);
  const oAuthRef = useRef(oAuth);
  const solutionShowRef = useRef(solution.show);
  const translationsRef = useRef(translations);
  const cookiesRef = useRef(cookies);
  const pendingMessageRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    oAuthRef.current = oAuth;
  }, [oAuth]);
  useEffect(() => {
    solutionShowRef.current = solution.show;
  }, [solution.show]);
  useEffect(() => {
    translationsRef.current = translations;
  }, [translations]);
  useEffect(() => {
    cookiesRef.current = cookies;
  }, [cookies]);

  const processSolutionMessage = useCallback(
    (json, filename) => {
      const currentTranslations = translationsRef.current;
      try {
        const { parsed, isUnassignedOnly } = parseSolutionJson(json);
        batch(() => {
          dispatch(setSolutionJson({ jsonNoTerritories: false, json: false }));
          dispatch(setTourParameter({ ordersMode: METHODS.JSON_IMPORT }));
          clearAll(dispatch, !userRef.current.hasStateMemory, false, solutionShowRef.current);
          dispatch(setSolutionParam({ param: { name: filename } }));
          if (isUnassignedOnly) {
            dispatch(
              setSolutionParam({
                param: { notices: parsed.notices || [], unassigned: parsed.unassigned },
              }),
            );
          }
          integrateFileText(
            json,
            'upload',
            filename,
            userRef.current,
            cookiesRef.current,
            oAuthRef.current,
            dispatch,
            true,
            currentTranslations.error,
            solutionShowRef.current,
            false,
            false,
            false,
            currentTranslations.map.depotMarkerTrans,
            currentTranslations.map.returnLocationMarkerTrans,
          );
        });
      } catch (err) {
        dispatch(setError(err));
      }
    },
    [dispatch],
  );

  const processTwoFilesMessage = useCallback(
    (files) => {
      const currentTranslations = translationsRef.current;
      batch(() => {
        dispatch(setSolutionJson({ jsonNoTerritories: false, json: false }));
        dispatch(setTourParameter({ ordersMode: METHODS.JSON_IMPORT }));
        clearAll(dispatch, !userRef.current.hasStateMemory, false, solutionShowRef.current);
      });
      handleTwoFiles(
        files,
        userRef.current,
        cookiesRef.current,
        oAuthRef.current,
        dispatch,
        currentTranslations.error,
        solutionShowRef.current,
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (oAuth && pendingMessageRef.current) {
      const { type, json, filename, files } = pendingMessageRef.current;
      pendingMessageRef.current = null;
      if (type === 'load-files') {
        setTimeout(() => processTwoFilesMessage(files), WIZARD_INIT_SETTLE_MS);
      } else {
        setTimeout(() => processSolutionMessage(json, filename), WIZARD_INIT_SETTLE_MS);
      }
    }
  }, [oAuth, processSolutionMessage, processTwoFilesMessage]);

  useEffect(() => {
    const handler = (event) => {
      const {
        type,
        solution: msgSolution,
        problem: msgProblem,
        filename: msgFilename,
        solutionFilename,
        problemFilename,
      } = event.data || {};

      if (type === 'load-files' && (msgProblem || msgSolution)) {
        const files = buildPostMessageFiles({
          problem: msgProblem,
          solution: msgSolution,
          problemFilename,
          solutionFilename,
        });

        if (files.length === 2) {
          pendingMessageRef.current = { type: 'load-files', files };
          if (!oAuthRef.current) return;
          pendingMessageRef.current = null;
          setTimeout(() => processTwoFilesMessage(files), WIZARD_INIT_SETTLE_MS);
          return;
        }

        if (files.length === 1 && files[0].fileContent.tours) {
          const json = JSON.stringify(files[0].fileContent, null, 2);
          const name = files[0].filename;
          pendingMessageRef.current = { type: 'load-solution', json, filename: name };
          if (!oAuthRef.current) return;
          pendingMessageRef.current = null;
          setTimeout(() => processSolutionMessage(json, name), WIZARD_INIT_SETTLE_MS);
          return;
        }
      }

      if (type === 'load-solution' && msgSolution) {
        const json = JSON.stringify(msgSolution, null, 2);
        const name = msgFilename || 'solution.json';
        pendingMessageRef.current = { type: 'load-solution', json, filename: name };
        if (!oAuthRef.current) return;
        pendingMessageRef.current = null;
        setTimeout(() => processSolutionMessage(json, name), WIZARD_INIT_SETTLE_MS);
      }
    };
    window.addEventListener('message', handler);
    if (window.opener) {
      window.opener.postMessage({ type: 'avalon-ready' }, '*');
    }
    return () => window.removeEventListener('message', handler);
  }, [processSolutionMessage, processTwoFilesMessage]);
};

export default usePostMessage;
